import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { ApiError, creerClientApi, type ClientApi, type ReponseConnexion } from './api.ts';

/**
 * Session utilisateur.
 *
 * Trois états, pas deux : « en cours de vérification », « connecté »,
 * « déconnecté ». Le premier est indispensable — au chargement de la page, le
 * jeton n'est plus en mémoire et le rafraîchissement n'a pas encore répondu.
 * Sans cet état, l'application afficherait brièvement l'écran de connexion à
 * un utilisateur déjà connecté, à chaque rechargement.
 */

export type EtatSession = 'verification' | 'connecte' | 'deconnecte';

/** Jeton d'une consultation du support, gardé le temps de l'onglet. */
const CLE_IMITATION = 'journal-imitation';

export interface DefiMfa {
  challengeToken: string;
}

interface ContexteSession {
  etat: EtatSession;
  tenantId: string | null;
  api: ClientApi;
  /** Renvoie un défi si la double authentification est active. */
  connecter: (email: string, motDePasse: string) => Promise<DefiMfa | null>;
  validerSecondFacteur: (defi: DefiMfa, code: string) => Promise<void>;
  deconnecter: () => Promise<void>;
  /**
   * La session a pris fin SANS qu'on l'ait demandé : jeton expiré, session
   * fermée depuis un autre appareil. L'écran de connexion le dit — sans quoi
   * l'enseignant revenu de pause se retrouvait devant le formulaire sans
   * comprendre pourquoi.
   */
  sessionExpiree: boolean;
}

const Contexte = createContext<ContexteSession | null>(null);

export function FournisseurSession({
  children, client,
}: {
  children: ReactNode;
  /** Injectable pour les tests. */
  client?: ClientApi;
}) {
  const api = useMemo(() => client ?? creerClientApi(), [client]);
  const [etat, setEtat] = useState<EtatSession>('verification');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [sessionExpiree, setSessionExpiree] = useState(false);

  // Au chargement : tenter de restaurer depuis le cookie HttpOnly.
  useEffect(() => {
    /*
     * ── Consultation par le support ─────────────────────────────────────────
     * Le back-office ouvre cet onglet avec le jeton dans le FRAGMENT de
     * l'adresse (`#imitation=…`) : un fragment n'est jamais envoyé au serveur,
     * ni écrit dans un journal d'accès. Il est retiré de la barre d'adresse
     * aussitôt. Le serveur refuse toute écriture à ce jeton ; l'écran le dit
     * par un bandeau permanent.
     *
     * Gardé dans le `sessionStorage` de l'ONGLET, et non en mémoire seule :
     * changer de classe recharge la page, et le rechargement refermait la
     * consultation — impossible de passer d'une classe à l'autre dans une
     * école. L'onglet fermé, il disparaît ; il expire de toute façon en quinze
     * minutes, et ne permet que de lire.
     */
    const fragment = /[#&]imitation=([A-Za-z0-9._-]+)/.exec(window.location.hash);
    let jetonSupport: string | null = fragment?.[1] ?? null;
    if (jetonSupport) {
      try { globalThis.sessionStorage?.setItem(CLE_IMITATION, jetonSupport); } catch { /* navigation privée */ }
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    } else {
      try { jetonSupport = globalThis.sessionStorage?.getItem(CLE_IMITATION) ?? null; } catch { jetonSupport = null; }
    }
    if (jetonSupport) {
      api.ouvrirImitation(jetonSupport);
      setEtat('connecte');
      return;
    }
    let annule = false;
    void api.restaurerSession().then((ok) => {
      if (!annule) setEtat(ok ? 'connecte' : 'deconnecte');
    });
    return () => { annule = true; };
  }, [api]);

  // Une session révoquée ailleurs (déconnexion depuis un autre appareil,
  // exclusion d'un espace) doit se refléter ici sans rechargement.
  useEffect(() => api.surDeconnexion(() => {
    // Consultation expirée : on n'y revient pas au rechargement suivant.
    try { globalThis.sessionStorage?.removeItem(CLE_IMITATION); } catch { /* rien à retirer */ }
    setSessionExpiree(true);
    setEtat('deconnecte');
    setTenantId(null);
  }), [api]);

  const connecter = useCallback(async (email: string, motDePasse: string) => {
    const reponse = await api.requete<ReponseConnexion>('/auth/login', {
      methode: 'POST',
      corps: { email, password: motDePasse },
      // Un mot de passe erroné renvoie 401 : tenter un rafraîchissement là
      // masquerait l'erreur derrière une déconnexion inexpliquée.
      sansRafraichissement: true,
    });

    if (reponse.mfaRequired && reponse.challengeToken) {
      return { challengeToken: reponse.challengeToken };
    }
    if (reponse.accessToken) {
      api.definirJeton(reponse.accessToken);
      setTenantId(reponse.tenantId ?? null);
      setSessionExpiree(false);
      setEtat('connecte');
    }
    return null;
  }, [api]);

  const validerSecondFacteur = useCallback(async (defi: DefiMfa, code: string) => {
    const reponse = await api.requete<ReponseConnexion>('/auth/login/verify', {
      methode: 'POST',
      corps: { challengeToken: defi.challengeToken, code },
      sansRafraichissement: true,
    });
    if (reponse.accessToken) {
      api.definirJeton(reponse.accessToken);
      setTenantId(reponse.tenantId ?? null);
      setSessionExpiree(false);
      setEtat('connecte');
    }
  }, [api]);

  const deconnecter = useCallback(async () => {
    // L'échec de l'appel ne doit pas empêcher la déconnexion locale : sinon
    // l'utilisateur reste bloqué sur une session qu'il veut quitter.
    try {
      await api.requete('/auth/logout', { methode: 'POST', sansRafraichissement: true });
    } catch { /* on ferme malgré tout */ }
    api.definirJeton(null);
    try { globalThis.sessionStorage?.removeItem(CLE_IMITATION); } catch { /* rien à retirer */ }
    setTenantId(null);
    setSessionExpiree(false);
    setEtat('deconnecte');
  }, [api]);

  const valeur = useMemo(
    () => ({ etat, tenantId, api, connecter, validerSecondFacteur, deconnecter, sessionExpiree }),
    [etat, tenantId, api, connecter, validerSecondFacteur, deconnecter, sessionExpiree],
  );
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useSession(): ContexteSession {
  const contexte = useContext(Contexte);
  if (!contexte) throw new Error('useSession doit être utilisé dans un FournisseurSession');
  return contexte;
}

/** Transforme une erreur en message affichable, sans jamais rester muet. */
export function messageErreur(erreur: unknown): string {
  if (erreur instanceof ApiError) return erreur.message;
  if (erreur instanceof Error) return erreur.message;
  return 'Une erreur est survenue.';
}
